/**
 * Award-data SSOT — AIHW Quality Indicator (QI) Program + QFR
 *
 * AIHW National Aged Care Mandatory Quality Indicator Program — quarterly
 * submission obligations under the Aged Care Act 2024 + Aged Care Rules 2025.
 *
 * NOTE: This is regulator/program data, NOT a Fair Work modern award. It
 * sits in `award-data/` alongside the FWC awards because all regulatory
 * surfaces share the same SSOT discipline + provenance requirements.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * INDEXATION CADENCE
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   - QI Program Manual republished as AIHW issues new versions (currently v4.0).
 *   - QI Benchmarks refreshed quarterly via AIHW Gen quarterly publication
 *     (https://www.gen-agedcaredata.gov.au).
 *   - QI Submission deadlines are calendar-fixed (21st of month after quarter end).
 *
 * Source: AIHW QI Program Manual 4.0 Part A + AIHW Residential Aged Care
 *         Quality Indicators supplementary data tables.
 */

import { DEFAULT_TZ } from '../timezone';

// ═══════════════════════════════════════════════════════════════════════
// Polypharmacy threshold (Indicator 5 — Medication Management)
// ═══════════════════════════════════════════════════════════════════════

/**
 * AIHW polypharmacy threshold — confirmed against AIHW QI Program Manual 4.0
 * Part A, Section 13 ("Medication management - polypharmacy"), Figure 2 page 6:
 * "Percentage of care recipients who were prescribed nine or more medications".
 *
 * Source: docs/regulatory-sources/aihw-qi-program-manual-v4.0-part-a.pdf.
 * Verified: 2026-04-07.
 */
export const QI_POLYPHARMACY_THRESHOLD = 9;

// ═══════════════════════════════════════════════════════════════════════
// QI quarterly submission deadlines
// ═══════════════════════════════════════════════════════════════════════

/**
 * QI Program quarterly submission deadlines — 21st of the month following
 * each quarter end.
 *
 * Late submissions trigger Standard 2 compliance review by ACQSC.
 * Submission via GPMS (Government Provider Management System) Quality
 * Indicators application.
 *
 * Source: AIHW QI Program Manual 4.0 Part A Section 3 + Figure 3 page 9.
 */
export const QI_SUBMISSION_DEADLINES = {
  Q1: { period: 'Jul-Sep', dueMonth: 'October', dueDay: 21 },
  Q2: { period: 'Oct-Dec', dueMonth: 'January', dueDay: 21 },
  Q3: { period: 'Jan-Mar', dueMonth: 'April', dueDay: 21 },
  Q4: { period: 'Apr-Jun', dueMonth: 'July', dueDay: 21 },
} as const;

export type QiQuarterKey = 'Q1' | 'Q2' | 'Q3' | 'Q4';

/**
 * Returns the most recently completed AIHW QI reporting quarter as of the
 * given date, along with the reporting-period start/end and the GPMS
 * submission deadline (21st of the month after the quarter end).
 *
 * This is the quarter whose DATA should currently be prepared/submitted:
 *   - 1 Jan – 31 Mar  (Q3) → due 21 April
 *   - 1 Apr – 30 Jun  (Q4) → due 21 July
 *   - 1 Jul – 30 Sep  (Q1) → due 21 October
 *   - 1 Oct – 31 Dec  (Q2) → due 21 January (next calendar year)
 *
 * All dates computed in the tenant's local timezone.
 *
 * Source: AIHW QI Program Manual 4.0 Part A §3 + Figure 3 (p.9).
 */
export function getCurrentQiSubmissionWindow(
  asOfDate: Date,
  tz: string = DEFAULT_TZ,
): {
  quarter: QiQuarterKey;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
} {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
  });
  const parts = fmt.formatToParts(asOfDate);
  const year = Number(parts.find((p) => p.type === 'year')!.value);
  const month = Number(parts.find((p) => p.type === 'month')!.value);

  let quarter: QiQuarterKey;
  let periodStart: string;
  let periodEnd: string;
  const dueYear = year;
  let dueMonth: number;
  if (month >= 1 && month <= 3) {
    quarter = 'Q2';
    periodStart = `${year - 1}-10-01`;
    periodEnd = `${year - 1}-12-31`;
    dueMonth = 1;
  } else if (month >= 4 && month <= 6) {
    quarter = 'Q3';
    periodStart = `${year}-01-01`;
    periodEnd = `${year}-03-31`;
    dueMonth = 4;
  } else if (month >= 7 && month <= 9) {
    quarter = 'Q4';
    periodStart = `${year}-04-01`;
    periodEnd = `${year}-06-30`;
    dueMonth = 7;
  } else {
    quarter = 'Q1';
    periodStart = `${year}-07-01`;
    periodEnd = `${year}-09-30`;
    dueMonth = 10;
  }

  const dueDate = `${dueYear}-${String(dueMonth).padStart(2, '0')}-21`;
  return { quarter, periodStart, periodEnd, dueDate };
}

// ═══════════════════════════════════════════════════════════════════════
// QFR (Quarterly Financial Report) submission deadlines
// ═══════════════════════════════════════════════════════════════════════

/**
 * Quarterly Financial Report (QFR) submission deadlines — separate from QI
 * deadlines. The department extracts care minutes (enrolled nursing, allied
 * health, lifestyle officer) from QFR data, so QFR submission must precede
 * the QI dashboard population for those indicators.
 *
 * Source: AIHW QI Manual 4.0 Part A page 9 table.
 */
export const QFR_SUBMISSION_DEADLINES = {
  Q1: { period: 'Jul-Sep', dueDate: '4 November' },
  Q2: { period: 'Oct-Dec', dueDate: '14 February' },
  Q3: { period: 'Jan-Mar', dueDate: '5 May' },
  Q4: { period: 'Apr-Jun', dueDate: '4 August' },
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Weight loss thresholds (Indicator 3 — Unplanned Weight Loss)
// ═══════════════════════════════════════════════════════════════════════

/**
 * AIHW Quality Indicator — unplanned weight loss thresholds.
 *
 *  - SIGNIFICANT unplanned weight loss: ≥5% comparing the resident's last
 *    weight of the current quarter to the last weight of the previous quarter
 *    (a 3-month / quarterly window).
 *  - CONSECUTIVE unplanned weight loss: weight loss of ANY amount recorded in
 *    each of 3 consecutive months within the quarter.
 *
 * The previous values (1-month/5%, 6-month/10%) were the MUST clinical malnutrition
 * screening definitions, NOT the AIHW QI definitions. Replaced 2026-04-07.
 *
 * Source: AIHW QI Program Manual 4.0 (health.gov.au).
 */
export const QI_WEIGHT_LOSS_THRESHOLDS = {
  significant: {
    /** ≥5% loss when comparing last-of-quarter vs last-of-prior-quarter weight */
    percentageThreshold: 5,
    comparisonWindow: 'quarter' as const,
  },
  consecutive: {
    /** Any amount of loss recorded in EACH of 3 consecutive months within the quarter */
    monthsRequired: 3,
    anyAmountLoss: true,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════
// QI national benchmarks
// ═══════════════════════════════════════════════════════════════════════

/**
 * AIHW National Aged Care Mandatory Quality Indicator Program — national benchmarks.
 *
 * NOT all indicators are percentages — some are care minutes per resident per
 * day. The `unit` field disambiguates. Keys match the prod `qi_domain` enum
 * exactly (snake_case) so dashboard lookups by enum value work directly.
 *
 * Source: AIHW Residential Aged Care Quality Indicators supplementary data
 * tables for Q1 2025-26 (July to September 2025), published February 2026.
 * Verified: 2026-04-07.
 *
 * Indexation: re-published quarterly. The application layer should refresh
 * these values from each new AIHW Gen quarterly publication.
 */
export type QIBenchmark = {
  national: number;
  unit: '%' | 'min/resident/day';
  quarter: string;
  /** Optional sub-indicator definition for clarity (e.g., "polypharmacy" within medication_management) */
  measure?: string;
};

export const QI_BENCHMARKS: Record<string, QIBenchmark> = {
  // 9 original indicators (pre-1 April 2025 expansion)
  /** Pressure injuries acquired in residential aged care (not pre-existing) */
  pressure_injuries: {
    national: 1.1,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'acquired in care, ≥1 PI',
  },
  /** Restrictive practices (any). Note: prod enum still labels this `physical_restraint`
   *  even though AIHW reports the broader "restrictive practices" measure covering
   *  both physical and chemical. Plan to rename the enum value to `restrictive_practices`
   *  in a future migration; until then this key matches the existing prod enum. */
  physical_restraint: {
    national: 21.4,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'restrictive practices (incl PRN)',
  },
  /** Significant unplanned weight loss (≥5% over a quarterly comparison) */
  unplanned_weight_loss: {
    national: 7.7,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'significant ≥5% over quarter',
  },
  /** Falls resulting in major injury (subset of total falls) */
  falls_major_injury: {
    national: 1.7,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'falls with major injury',
  },
  /** Polypharmacy (≥9 medications) — the headline medication management indicator */
  medication_management: {
    national: 35.2,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'polypharmacy ≥9 meds',
  },
  /** Hospitalisation — emergency department presentations */
  hospitalisations: {
    national: 13.6,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'ED presentation',
  },
  /** Incontinence — proportion of residents with any incontinence */
  incontinence_care: {
    national: 75.5,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'any incontinence',
  },
  /** Workforce — quarterly staff turnover (eligible staff who stopped working) */
  workforce: { national: 4.7, unit: '%', quarter: 'Jul-Sep 2025', measure: 'quarterly turnover' },
  /** Consumer experience — proportion reporting positive overall experience. */
  consumer_experience: {
    national: 75.0,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'positive experience',
  },

  // 5 new indicators added 1 April 2025
  /** Activities of daily living — proportion experiencing decline */
  activities_of_daily_living: {
    national: 20.2,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'decline in ADLs',
  },
  /** Quality of life — proportion reporting positive QoL */
  quality_of_life: {
    national: 75.0,
    unit: '%',
    quarter: 'Jul-Sep 2025',
    measure: 'positive QoL self-report',
  },
  /** Enrolled nursing care minutes — average minutes per resident per day */
  enrolled_nursing: {
    national: 12.2,
    unit: 'min/resident/day',
    quarter: 'Jul-Sep 2025',
    measure: 'EN care minutes',
  },
  /** Allied health care minutes — average minutes per resident per day */
  allied_health: {
    national: 4.8,
    unit: 'min/resident/day',
    quarter: 'Jul-Sep 2025',
    measure: 'allied health care minutes',
  },
  /** Lifestyle officer care minutes — average minutes per resident per day */
  lifestyle_officer: {
    national: 7.2,
    unit: 'min/resident/day',
    quarter: 'Jul-Sep 2025',
    measure: 'lifestyle officer care minutes',
  },
} as const;
